/**

 * Hook pour gérer les données d'affiliation sans dépendance circulaire
 * Ce hook ne dépend pas de useAuth pour éviter les dépendances circulaires
 */

import { useState, useEffect, useCallback } from 'react';
import { UserAffiliation, CreateUserAffiliationRequest, UpdateUserAffiliationRequest } from '@/models/userAffiliation';
import { UserAffiliationRepository } from '@/services/firebase/user-affiliation-repository';

interface UseUserAffiliationDataReturn {
  // État
  affiliations: UserAffiliation[];
  loading: boolean;
  error: string | null;
  
  // Actions
  createAffiliation: (request: CreateUserAffiliationRequest) => Promise<UserAffiliation>;
  updateAffiliation: (affiliationId: string, request: UpdateUserAffiliationRequest) => Promise<UserAffiliation>;
  deleteAffiliation: (affiliationId: string) => Promise<void>;
  updateLastActivity: (userId: string, organizationId: string) => Promise<void>;
  
  // Utilitaires
  getUserOrganizations: (customerId: string) => Promise<void>; // Accepte maintenant customerId directement
  getOrganizationUsers: (organizationId: string) => Promise<UserAffiliation[]>;
  hasAccess: (customerId: string, organizationId: string) => Promise<boolean>; // Accepte maintenant customerId directement
  hasPermission: (customerId: string, organizationId: string, permission: keyof UserAffiliation['permissions']) => Promise<boolean>; // Accepte maintenant customerId directement
  
  // Getters
  getAffiliationByOrganization: (organizationId: string) => UserAffiliation | undefined;
  getCurrentUserRole: (organizationId: string) => UserAffiliation['role'] | null; // Plus besoin de userId
  isCurrentUserAdmin: (organizationId: string) => boolean; // Plus besoin de userId
  isCurrentUserOwner: (organizationId: string) => boolean; // Plus besoin de userId
}

export const useUserAffiliationData = (): UseUserAffiliationDataReturn => {
  const [affiliations, setAffiliations] = useState<UserAffiliation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Charge les organisations d'un utilisateur (par customerId)
   */
  const getUserOrganizations = useCallback(async (customerId: string) => {
    if (!customerId) {
      setAffiliations([]);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const userAffiliations = await UserAffiliationRepository.getUserOrganizations(customerId);
      setAffiliations(userAffiliations);
    } catch (err) {
      console.error('Erreur lors du chargement des organisations:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Charge les utilisateurs d'une organisation
   */
  const getOrganizationUsers = useCallback(async (organizationId: string): Promise<UserAffiliation[]> => {
    setLoading(true);
    setError(null);
    
    try {
      const organizationUsers = await UserAffiliationRepository.getOrganizationUsers(organizationId);
      return organizationUsers;
    } catch (err) {
      console.error('Erreur lors du chargement des utilisateurs de l\'organisation:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crée une nouvelle affiliation
   */
  const createAffiliation = useCallback(async (request: CreateUserAffiliationRequest): Promise<UserAffiliation> => {
    setLoading(true);
    setError(null);
    
    try {
      const newAffiliation = await UserAffiliationRepository.create(request);
      
      // Mettre à jour la liste locale
      setAffiliations(prev => [newAffiliation, ...prev]);
      
      return newAffiliation;
    } catch (err) {
      console.error('Erreur lors de la création de l\'affiliation:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Met à jour une affiliation
   */
  const updateAffiliation = useCallback(async (affiliationId: string, request: UpdateUserAffiliationRequest): Promise<UserAffiliation> => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedAffiliation = await UserAffiliationRepository.update(affiliationId, request);
      
      // Mettre à jour la liste locale
      setAffiliations(prev => 
        prev.map(affiliation => 
          affiliation.id === affiliationId ? updatedAffiliation : affiliation
        )
      );
      
      return updatedAffiliation;
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'affiliation:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Supprime une affiliation
   */
  const deleteAffiliation = useCallback(async (affiliationId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      await UserAffiliationRepository.delete(affiliationId);
      
      // Mettre à jour la liste locale
      setAffiliations(prev => prev.filter(affiliation => affiliation.id !== affiliationId));
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'affiliation:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Met à jour la dernière activité (par customerId)
   * Note: Cette méthode est legacy, devrait utiliser customerId directement
   */
  const updateLastActivity = useCallback(async (customerIdOrFirebaseUid: string, organizationId: string): Promise<void> => {
    try {
      // Si c'est déjà un customerId (format attendu), l'utiliser directement
      // Sinon, c'est un firebaseUid legacy - on devrait utiliser le contexte
      // Pour l'instant, on assume que c'est un customerId
      await UserAffiliationRepository.updateLastActivity(customerIdOrFirebaseUid, organizationId);
      
      // Mettre à jour la liste locale
      setAffiliations(prev => 
        prev.map(affiliation => 
          affiliation.organizationId === organizationId 
            ? { ...affiliation, lastActivityAt: new Date() }
            : affiliation
        )
      );
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'activité:', err);
    }
  }, []);

  /**
   * Vérifie si un utilisateur a accès à une organisation (par customerId)
   */
  const hasAccess = useCallback(async (customerId: string, organizationId: string): Promise<boolean> => {
    try {
      return await UserAffiliationRepository.hasAccess(customerId, organizationId);
    } catch (err) {
      console.error('Erreur lors de la vérification d\'accès:', err);
      return false;
    }
  }, []);

  /**
   * Vérifie si un utilisateur a une permission spécifique (par customerId)
   */
  const hasPermission = useCallback(async (
    customerId: string, 
    organizationId: string, 
    permission: keyof UserAffiliation['permissions']
  ): Promise<boolean> => {
    try {
      return await UserAffiliationRepository.hasPermission(customerId, organizationId, permission);
    } catch (err) {
      console.error('Erreur lors de la vérification de permission:', err);
      return false;
    }
  }, []);

  /**
   * Récupère l'affiliation pour une organisation spécifique
   */
  const getAffiliationByOrganization = useCallback((organizationId: string): UserAffiliation | undefined => {
    return affiliations.find(affiliation => 
      affiliation.organizationId === organizationId && affiliation.status === 'active'
    );
  }, [affiliations]);

  /**
   * Récupère le rôle de l'utilisateur pour une organisation
   * Note: Cette méthode nécessite que les affiliations soient déjà chargées
   */
  const getCurrentUserRole = useCallback((organizationId: string): UserAffiliation['role'] | null => {
    // Cette méthode nécessite que les affiliations soient déjà chargées
    const affiliation = affiliations.find(affiliation => 
      affiliation.organizationId === organizationId && 
      affiliation.status === 'active'
    );
    return affiliation ? affiliation.role : null;
  }, [affiliations]);

  /**
   * Vérifie si l'utilisateur est administrateur
   */
  const isCurrentUserAdmin = useCallback((organizationId: string): boolean => {
    const role = getCurrentUserRole(organizationId);
    return role === 'admin' || role === 'owner';
  }, [getCurrentUserRole]);

  /**
   * Vérifie si l'utilisateur est propriétaire
   */
  const isCurrentUserOwner = useCallback((organizationId: string): boolean => {
    const role = getCurrentUserRole(organizationId);
    return role === 'owner';
  }, [getCurrentUserRole]);

  return {
    // État
    affiliations,
    loading,
    error,
    
    // Actions
    createAffiliation,
    updateAffiliation,
    deleteAffiliation,
    updateLastActivity,
    
    // Utilitaires
    getUserOrganizations,
    getOrganizationUsers,
    hasAccess,
    hasPermission,
    
    // Getters
    getAffiliationByOrganization,
    getCurrentUserRole,
    isCurrentUserAdmin,
    isCurrentUserOwner,
  };
};
